import React, { FC, useContext, useEffect, useRef, useState } from "react"

import { LeftOutlined } from "@ant-design/icons"
import { Button, Card, Descriptions, Empty, Flex, Grid, Skeleton, Space, Tag, UploadFile } from "antd"
import { Link, useNavigate, useParams } from "react-router-dom"
import { uid } from "uid"

import classes from "@/_styles/cardById.module.scss"
import variables from "@/_styles/variables.module.scss"
import { ColorifiedButton } from "@/components/_Shared/Button/ColorifiedButton"
import { PicturesUpload } from "@/components/_Shared/PicturesUpload"
import { TransportAddForm, TransportAddFormProps } from "@/components/Transport/TransportAddForm"
import { Resource, Scope } from "@/constants/permission"
import { CustomerContext } from "@/context/CustomerContext"
import { useNotificationContext } from "@/context/NotificationContext"

import { useTransportByIdQuery } from "@/hook/Dictionaries/useTransportQuery"
import { useTransportsMutation } from "@/hook/Dictionaries/useTransportsMutation"
import { usePermission } from "@/hook/usePermission"

import {
  ITransportCertificateType,
  ITransportChangeVerificationStatus,
  ITransportClass,
  TransportCertificateTypesGenitives,
  VerificationStatus
} from "@/types/ITransport"

import { paths } from "@/router/paths"

import { uploadFileConvert } from "@/utils/uploadFileConvert"


const TransportById: FC = () => {
  const customerContext = useContext(CustomerContext)

  const transportComponent = useRef<TransportAddFormProps>(null)
  const navigate = useNavigate()
  const { id } = useParams<{ id?: string }>()
  const { hasPermission } = usePermission()
  const { changeVerificationMutation } = useTransportsMutation()
  const { refetchCountAwaitsVerification } =useNotificationContext()

  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [adrFileList, setAdrFileList] = useState<UploadFile[]>([])

  const { data, isLoading, isError } = useTransportByIdQuery(Number(id))

  const showAdditionalData = data?.response.class !== ITransportClass.Truck

  const { useBreakpoint } = Grid
  const screens = useBreakpoint()
  const direction = screens.xs ? "vertical" : "horizontal"

  useEffect(() => {
    if (data?.response?.stsScans) {
      const newPromises = data.response.stsScans.map((file) =>
        uploadFileConvert({
          url: file.content,
          name: file.name,
          uid: uid()
        })
      )
      Promise.all(newPromises).then((res) => {
        onFileListChange(res)
      })
    }
  }, [data?.response.stsScans])

  useEffect(() => {
    const adrList = data?.response?.adrScans ?? data?.response?.adrScans
    if (adrList) {
      const newPromises = adrList.map((file) =>
        uploadFileConvert({
          url: file.content,
          name: file.name,
          uid: uid()
        })
      )
      Promise.all(newPromises).then((res) => {
        onAdrFileListChange(res)
      })
    }
  }, [data?.response?.adrScans])

  const onFileListChange = (file: UploadFile | UploadFile[]) => {
    if (Array.isArray(file)) {
      setFileList(file)
    } else {
      setFileList((prevState) => [...prevState, file])
    }
  }

  const onAdrFileListChange = (file: UploadFile | UploadFile[]) => {
    if (Array.isArray(file)) {
      setAdrFileList(file)
    } else {
      setAdrFileList((prevState) => [...prevState, file])
    }
  }

  const onBackHandler = () => {
    navigate(paths.transport)
  }

  const confirmVerification = async () => {
    const reqeust: ITransportChangeVerificationStatus = {
      id : data!.response.id,
      status : VerificationStatus.Confirmed
    }
    await changeVerificationMutation.mutateAsync(reqeust)
    refetchCountAwaitsVerification()
  }

  const rejectVerification = async () =>{
    const request: ITransportChangeVerificationStatus ={
      id: data!.response.id,
      status: VerificationStatus.Rejected
    } 

    await changeVerificationMutation.mutateAsync(request)
    refetchCountAwaitsVerification()
  }

  const openCustomerEdit = () => {
    transportComponent.current?.open(Number(id), true)
  }

  if (isError) {
    return <Empty description="Данные Транспорта не найдены" />
  }

  const certificateTypeGenitive = ((data?.response.certificateType !== undefined) && (data.response.certificateType !== null))
    ? " " + TransportCertificateTypesGenitives[data.response.certificateType]
    : ""

  const certificateTypeDative = ((data?.response.certificateType !== undefined) && (data.response.certificateType !== null))
  ? data.response.certificateType === ITransportCertificateType.Calibration
    ? " сертификату о калибровке" : " свидетельству о поверке" : ""
  
  return (
    <>
      <TransportAddForm ref={transportComponent}/>
      <Skeleton loading={isLoading}>
        {data && (
          <>
            <Card className={classes["card"]}>
              <Space direction="vertical" className={classes["wrapper"]}>
                <Descriptions
                  title={
                    <div className={classes["header"]}>
                      <Flex align="center" gap="20px">
                        <ColorifiedButton 
                          themecolor="blue"
                          type="primary"
                          shape="circle"
                          size="small"
                          icon={<LeftOutlined />}
                          onClick={onBackHandler}
                        />
                        <span>Транспорт {data.response.number}</span>
                      </Flex>
                      <div>
                        {!customerContext.isCustomer() &&
                          data.response.clients.map((client) => {
                            return (
                              <Tag key={"client-tag"+client.id}>
                                <Link to={paths.customerClientById(client.id)}>
                                  {client.name}
                                </Link>
                              </Tag>
                            )
                          })}
                      </div>
                    </div>
                  }
                  layout="vertical"
                  column={5}
                  className={classes["descriptions"]}
                  labelStyle={{ 
                    fontWeight: 600, 
                    color: variables.tableSubTitleColor, 
                    marginBottom: "-30px"
                  }}
                  contentStyle={{
                    paddingBottom: "15px"
                  }}
                >
                  <Descriptions.Item label="Номер записи">{data.response.id}</Descriptions.Item>
                  <Descriptions.Item label="Гос.номер транспорта">{data.response.number}</Descriptions.Item>
                  {data.response.region && <Descriptions.Item label="Регион">{data.response.region}</Descriptions.Item>}
                  <Descriptions.Item label="Марка">{data.response.brand}</Descriptions.Item>
                  <Descriptions.Item label="Модель">{data.response.model}</Descriptions.Item>
                  <Descriptions.Item label="Вид транспорта">{data.response.className}</Descriptions.Item>
                  <Descriptions.Item label="Цвет">{data.response.color}</Descriptions.Item>
                  <Descriptions.Item label="VIN (в случае, номер шасси)">{data.response.vin}</Descriptions.Item>
                  <Descriptions.Item label="Тип транспорта">{data.response.type}</Descriptions.Item>
                  <Descriptions.Item label="Собственник">{data.response.owner}</Descriptions.Item>
                  <Descriptions.Item label="Масса без нагрузки, кг">{data.response.weightWithoutLoad} кг</Descriptions.Item>
                  <Descriptions.Item label="Максимальная разрешенная масса, кг">{data.response.permittedMaximumMass} кг</Descriptions.Item>
                  <Descriptions.Item label="Колличество осей">{data.response.axlesNumber}</Descriptions.Item>
                  <Descriptions.Item label="Перевозчик">{data.response.carrierName}</Descriptions.Item>
                  {
                    showAdditionalData &&
                      <>
                        <Descriptions.Item label={"Номер" + certificateTypeGenitive}>{data.response.certificateNumber}</Descriptions.Item>
                        <Descriptions.Item label={"Дата начала действия" + certificateTypeGenitive}>{data.response.dateBeginningCertificate}</Descriptions.Item>
                        <Descriptions.Item label={"Дата окончания действия" + certificateTypeGenitive}>{data.response.dateExpirationCertificate}</Descriptions.Item>
                        <Descriptions.Item label={"Литраж по" + certificateTypeDative}>{data.response.liter}</Descriptions.Item>
                      </>
                  }
                  <Descriptions.Item label={"Добавлено"}>{data.response.createdByName}</Descriptions.Item>
                  <Descriptions.Item label = "Статус проверки">
                    <span style={{ wordBreak: "normal" }}>{data.response.verificationStatusText}</span>
                  </Descriptions.Item>
                </Descriptions>
              </Space>
            </Card>
            
            {
              showAdditionalData &&
                <Card title="Калибровка" className={classes["card"]}>
                  <Space direction="vertical" className={classes["wrapper"]}>
                    <Descriptions
                      layout="vertical"
                      column={4}
                      className={classes["descriptions"]}
                      labelStyle={{ 
                        fontWeight: 600, 
                        color: variables.tableSubTitleColor, 
                        marginBottom: "-30px"
                      }}
                      contentStyle={{
                        paddingBottom: "15px"
                      }}
                    >
                      <Descriptions.Item label="Секция 1">{data.response.section1}</Descriptions.Item>
                      <Descriptions.Item label="Секция 2">{data.response.section2}</Descriptions.Item>
                      <Descriptions.Item label="Секция 3">{data.response.section3}</Descriptions.Item>
                      <Descriptions.Item label="Секция 4">{data.response.section4}</Descriptions.Item>
                      <Descriptions.Item label="Секция 5">{data.response.section5}</Descriptions.Item>
                      <Descriptions.Item label="Секция 6">{data.response.section6}</Descriptions.Item>
                      <Descriptions.Item label="Секция 7">{data.response.section7}</Descriptions.Item>
                      <Descriptions.Item label="Секция 8">{data.response.section8}</Descriptions.Item>
                    </Descriptions>
                  </Space>
                </Card>
            }

            <Card title="Скан СТС" className={classes["card"]}>
              <Space direction="vertical" className={classes["wrapper"]}>
                <PicturesUpload readonly fileList={fileList} />
              </Space>
            </Card>

            <Card title="ДОПОГ" className={classes["card"]}>
              <Space direction="vertical" className={classes["wrapper"]}>
                <PicturesUpload readonly fileList={adrFileList} />
              </Space>
            </Card>

            <Card title="Статус проверки" className={classes["card"]}>
              <Space direction={direction}>
                <span style={{ wordBreak: "normal" }}>{data.response.verificationStatusText}</span>
                {data.response.verificationStatus === VerificationStatus.Awaits && hasPermission(Resource.Transport, Scope.Actions) && (
                  <Space>
                    <Button onClick={confirmVerification} type="primary">подтвердить</Button>
                    <Button onClick={rejectVerification} danger>отклонить</Button>
                  </Space>
                )}
                {
                  (data.response.verificationStatus === VerificationStatus.AwaitsForScans) && customerContext.isCustomer() &&
                  <Button onClick={openCustomerEdit} type="primary">
                    загрузить сканы
                  </Button>
                }
              </Space>
            </Card>
          </>
        )}
      </Skeleton>
    </>
  )
}

export default TransportById
